import {
  ComponentType,
  ReactNode,
  startTransition,
  useEffect,
  useState,
} from 'react';

import { PopoverProps } from '@mui/material';
import { usePopupState } from 'material-ui-popup-state/hooks';

import { DropdownInput } from './components/DropdownInput';
import { DropdownPopper } from './components/DropdownPopper';
import { DropdownRoot } from './components/DropdownRoot';
import { DropdownContext } from './DropdownContext';
import { AutocompleteOverrides, DropdownProps } from './types';
import { useDropdownWithAutocomplete } from './useDropdownWithAutocomplete';
import { toLegacySize } from './utils';
import { ListContext } from '../../lists/List';

export type AutocompleteProps<
  OptionItem = unknown,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
> = DropdownProps<OptionItem, OptionValue, SelectionType> &
  Pick<
    AutocompleteOverrides<OptionItem, OptionValue, SelectionType>,
    'searchInputProps'
  > & {
    PopoverComponent?: ComponentType<PopoverProps>;
    trigger?: ReactNode;
  };

export const Autocomplete = <
  OptionItem,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
>({
  PopoverComponent,
  trigger,
  searchInputProps,
  ...restProps
}: AutocompleteProps<OptionItem, OptionValue, SelectionType>) => {
  const dropdownProps = restProps as DropdownProps<
    OptionItem,
    OptionValue,
    SelectionType
  >;

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'autocomplete',
  });

  const [inputValue, setInputValue] = useState('');
  const isSingleSelection = dropdownProps.multiple !== true;
  const autocomplete = useDropdownWithAutocomplete<
    OptionItem,
    OptionValue,
    SelectionType
  >({
    dropdownProps,
    autocompleteOverrides: {
      onChange: (option: OptionItem) => {
        if (isSingleSelection) {
          if (option) {
            const valueResult =
              autocomplete.getters.getOptionSelectedValue(option);
            // Check if not a ReactNode
            setInputValue(typeof valueResult === 'string' ? valueResult : '');
          } else {
            setInputValue('');
          }

          dropdownProps.onChange?.(
            option,
            autocomplete.getters.getOptionValue(option)
          );
        }
      },
      open: popupState.isOpen,
      searchInputProps,
    } as AutocompleteOverrides<OptionItem, OptionValue, SelectionType>,
  });

  useEffect(() => {
    if (isSingleSelection) {
      const newValue =
        dropdownProps.value && autocomplete.states.selectedOption
          ? autocomplete.getters.getOptionSelectedValue(
              autocomplete.states.selectedOption
            )
          : '';
      // Check if not a ReactNode
      startTransition(() => {
        setInputValue(typeof newValue === 'string' ? newValue : '');
      });
    }
  }, [
    autocomplete.getters,
    autocomplete.states.selectedOption,
    dropdownProps,
    isSingleSelection,
  ]);

  return (
    <DropdownContext.Provider
      value={{
        dropdownProps,
        popupState,
        autocomplete,
      }}
    >
      <ListContext.Provider value={{ size: toLegacySize(dropdownProps.size) }}>
        <DropdownRoot>
          {trigger || (
            <DropdownInput
              inputProps={{
                value: inputValue,
              }}
            />
          )}
        </DropdownRoot>

        <DropdownPopper PopoverComponent={PopoverComponent} />
      </ListContext.Provider>
    </DropdownContext.Provider>
  );
};
